import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../ui/select";
import { 
  Car, Baby, Dog, Briefcase, Star, Phone, User, Edit, Loader2, AlertTriangle
} from "lucide-react";
import { protectionTypes } from "../../lib/designTokens";

const API = '/api';

// Icon mapping
const iconMap = {
  Car: Car,
  Baby: Baby,
  Dog: Dog,
  Briefcase: Briefcase,
  Star: Star
};

// Get icon component for protection type
const getTypeIcon = (type) => {
  const config = protectionTypes[type] || protectionTypes.vehicle;
  const IconComponent = iconMap[config.icon] || Car;
  return <IconComponent className="w-5 h-5" />;
};

// Get config for protection type
const getTypeConfig = (type) => {
  return protectionTypes[type] || protectionTypes.vehicle;
};

// Dynamic form field renderer
const FormField = ({ field, value, onChange }) => {
  const isPhone = field.type === "tel";
  
  if (field.type === "select") {
    return (
      <div className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <Select value={value || ""} onValueChange={(val) => onChange(field.name, val)}>
          <SelectTrigger className="h-11">
            <SelectValue placeholder={`Selecciona ${field.label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  
  if (field.type === "textarea") {
    return (
      <div className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <Textarea
          id={field.name}
          value={value || ""}
          onChange={(e) => onChange(field.name, e.target.value)}
          placeholder={field.placeholder}
          className="min-h-[80px]"
        />
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      <Label htmlFor={field.name}>
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <div className="relative">
        <Input
          id={field.name}
          type={field.type === "number" ? "number" : "text"}
          value={value || ""}
          onChange={(e) => onChange(field.name, e.target.value)}
          placeholder={field.placeholder}
          className="h-11"
          maxLength={isPhone ? 16 : undefined}
        />
      </div>
    </div>
  );
};

// Main component
const EditProtectionModal = ({ 
  open, 
  onOpenChange, 
  protection, 
  onSaved 
}) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  
  const protectionType = protection?.protection_type || "vehicle";
  const typeConfig = getTypeConfig(protectionType);
  
  // Initialize form data when protection changes
  useEffect(() => {
    if (protection && open) {
      const initialData = {};
      
      // Map protection fields to form
      typeConfig.fields.forEach(field => {
        // Handle different field name mappings
        let value = protection[field.name];
        
        // Special mappings for backwards compatibility
        if (field.name === "primary_phone" && !value) {
          // primary_contact can be either string or object {phone: "...", whatsapp: "..."}
          const pc = protection.primary_contact;
          if (typeof pc === 'object' && pc !== null) {
            value = pc.phone || pc.whatsapp || "";
          } else {
            value = pc || protection.phone || "";
          }
        }
        if (field.name === "object_type" && !value) {
          value = protection.type || "";
        }
        if (field.name === "pet_type" && !value) {
          value = protection.type || "";
        }
        
        initialData[field.name] = value || "";
      });
      
      setFormData(initialData);
      setErrors({});
    }
  }, [protection, open, typeConfig.fields]);
  
  // Handle field change
  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  // Validación internacional genérica (el teléfono no participa del flujo anónimo)
  const validateIntlContactPhone = (phone) => {
    if (!phone) return true; // Empty is OK for optional fields
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 15;
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    typeConfig.fields.forEach(field => {
      const value = formData[field.name];
      
      // Required validation
      if (field.required && !value?.trim()) {
        newErrors[field.name] = `${field.label} es obligatorio`;
        return;
      }
      
      // Phone validation (internacional)
      if (field.type === "tel" && value && !validateIntlContactPhone(value)) {
        newErrors[field.name] = "Número inválido (7 a 15 dígitos, puede incluir código de país)";
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      toast.error("Por favor corrige los errores");
      return;
    }
    
    setSaving(true);
    try {
      // Prepare data for API
      const updateData = {
        ...formData,
        protection_type: protectionType
      };
      
      // Map primary_phone back to primary_contact for backwards compatibility
      if (formData.primary_phone) {
        updateData.primary_contact = formData.primary_phone;
      }
      
      await axios.put(`${API}/vehicles/${protection.id}`, updateData);
      
      toast.success(`${typeConfig.label} actualizado correctamente`);
      
      // Call onSaved callback for parent to refresh data
      if (onSaved) {
        onSaved(updateData);
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving protection:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar");
    } finally {
      setSaving(false);
    }
  };
  
  // Group fields by category for better UX (iter64 — emergency-contacts feature removed)
  const groupFields = () => {
    const mainFields = [];
    const contactFields = [];

    typeConfig.fields.forEach(field => {
      if (field.name.includes('emergency')) {
        // iter64 — skip legacy emergency contact fields entirely
        return;
      } else if (field.name.includes('phone') || field.name === 'primary_contact') {
        contactFields.push(field);
      } else {
        mainFields.push(field);
      }
    });

    return { mainFields, contactFields };
  };

  const { mainFields, contactFields } = groupFields();
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getTypeIcon(protectionType)}
            <span>Editar {typeConfig.label}</span>
            <span className="text-2xl">{typeConfig.emoji}</span>
          </DialogTitle>
          <DialogDescription>
            Actualiza la información de tu {typeConfig.label.toLowerCase()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Main Info Section */}
          <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
              {getTypeIcon(protectionType)}
              Información del {typeConfig.label}
            </h4>
            <div className="grid gap-4">
              {mainFields.map(field => (
                <div key={field.name}>
                  <FormField
                    field={field}
                    value={formData[field.name]}
                    onChange={handleFieldChange}
                  />
                  {errors[field.name] && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {errors[field.name]}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Contact Section */}
          {contactFields.length > 0 && (
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Destinatarios de avisos
              </h4>
              <div className="grid gap-4">
                {contactFields.map(field => (
                  <div key={field.name}>
                    <FormField
                      field={field}
                      value={formData[field.name]}
                      onChange={handleFieldChange}
                    />
                    {errors[field.name] && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {errors[field.name]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* iter64 — Emergency contacts feature removed. The Emergency BUTTON on the scanner remains active via push notification. */}
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-[#1B84FF] hover:bg-[#0F6FDB] text-white"
            data-testid="save-edit-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Edit className="w-4 h-4 mr-2" />
                Guardar Cambios
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditProtectionModal;
